import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import {
  ConstructionScheduleForm,
  FiberScheduleForm,
  NetworkingInstallationsForm,
} from "./PhaseSchedulePrompt";
import {
  domainsFromProjectRow,
  mergeDomainFields,
  patchCommercialDomains,
  readConstructionSchedule,
  readFiberSchedule,
  readNetworkingInstallations,
} from "./commercialScheduleApi";
import { notifyNetworkingInstallationEmails } from "./networkingInstallationApi";

const EVENT_TYPE_LABELS = {
  construction: "Construction schedule",
  splicing: "Fiber splicing schedule",
  networking: "Networking installation",
};

const EVENT_TYPE_CHANGE_LOG_DOMAINS = {
  construction: [COMMERCIAL_DOMAINS.CONSTRUCTION],
  splicing: [COMMERCIAL_DOMAINS.FIBER],
  networking: [COMMERCIAL_DOMAINS.NETWORKING],
};

function parseNetworkingInstallIndex(eventKey) {
  if (eventKey == null || eventKey === "") return null;
  const m = String(eventKey).match(/^install-(\d+)$/);
  if (!m) return null;
  return Number(m[1]);
}

/**
 * Popup editor when a manager clicks a calendar event chip.
 */
export default function ScheduleCalendarEventModal({
  event,
  projectRow,
  onClose,
  onSaved,
  apiUrl,
  getAuthHeaders,
}) {
  const [domains, setDomains] = useState(() => domainsFromProjectRow(projectRow, projectRow?.acf_domains));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setDomains(domainsFromProjectRow(projectRow, projectRow?.acf_domains));
    setError("");
  }, [projectRow?.id, event?.eventType, event?.eventKey]);

  useEffect(() => {
    if (!event) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [event, onClose]);

  const eventType = event?.eventType;
  const title = EVENT_TYPE_LABELS[eventType] || "Schedule";
  const workOrder = event?.workOrder ?? "—";
  const customer = event?.customer ?? "—";

  const patchConstruction = (patch) => {
    setDomains((prev) => mergeDomainFields(prev, COMMERCIAL_DOMAINS.CONSTRUCTION, patch));
  };

  const patchFiber = (patch) => {
    setDomains((prev) => mergeDomainFields(prev, COMMERCIAL_DOMAINS.FIBER, patch));
  };

  const patchNetworkingInstallations = (installations) => {
    setDomains((prev) =>
      mergeDomainFields(prev, COMMERCIAL_DOMAINS.NETWORKING, { installations })
    );
  };

  const networkingInstallIndex = useMemo(
    () => (eventType === "networking" ? parseNetworkingInstallIndex(event?.eventKey) : null),
    [eventType, event?.eventKey]
  );

  const networkingInstallations = readNetworkingInstallations(domains);
  const focusedNetworkingRows = useMemo(() => {
    if (eventType !== "networking") return networkingInstallations;
    const idx = networkingInstallIndex;
    if (idx == null || idx < 0 || idx >= networkingInstallations.length) {
      return networkingInstallations;
    }
    return [networkingInstallations[idx]];
  }, [eventType, networkingInstallIndex, networkingInstallations]);

  const saveNetworkingInstallations = (nextRows) => {
    if (eventType !== "networking" || networkingInstallIndex == null) {
      patchNetworkingInstallations(nextRows);
      return;
    }
    const merged = [...networkingInstallations];
    if (networkingInstallIndex >= 0 && networkingInstallIndex < merged.length) {
      merged[networkingInstallIndex] = nextRows[0] || merged[networkingInstallIndex];
      patchNetworkingInstallations(merged);
      return;
    }
    patchNetworkingInstallations(nextRows);
  };

  if (!event || !projectRow) return null;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await patchCommercialDomains({
        projectId: projectRow.id,
        projectRow,
        domains,
        getAuthHeaders,
        apiUrl,
        source: "schedule_calendar",
        userEmail: getViewerEmailFromStorage(),
        changeLogDomains: EVENT_TYPE_CHANGE_LOG_DOMAINS[eventType] || null,
      });
      if (eventType === "networking") {
        await notifyNetworkingInstallationEmails({
          projectId: projectRow.id,
          getAuthHeaders,
          apiUrl,
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not save schedule.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="schedule-calendar-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="schedule-calendar-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-calendar-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="schedule-calendar-modal-title" className="schedule-calendar-modal-title">
          {title}
        </h2>
        <p className="schedule-calendar-modal-meta">
          <strong>{workOrder}</strong> · {customer}
        </p>

        <div className="phase-schedule-prompt-forms schedule-calendar-modal-forms">
          {eventType === "construction" ? (
            <ConstructionScheduleForm
              schedule={readConstructionSchedule(domains)}
              onPatch={patchConstruction}
              disabled={saving}
            />
          ) : null}
          {eventType === "splicing" ? (
            <FiberScheduleForm
              schedule={readFiberSchedule(domains)}
              onPatch={patchFiber}
              disabled={saving}
            />
          ) : null}
          {eventType === "networking" ? (
            <NetworkingInstallationsForm
              installations={focusedNetworkingRows}
              onChange={saveNetworkingInstallations}
              disabled={saving}
            />
          ) : null}
        </div>

        {error ? (
          <p className="project-detail-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="schedule-calendar-modal-actions">
          <button
            type="button"
            className="project-landing-edit-btn"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save schedule"}
          </button>
          <Link
            to={`/project/${projectRow.id}`}
            className="phase-schedule-project-link"
            onClick={onClose}
          >
            Open project
          </Link>
          <button type="button" className="email-triggers-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
